import React, { useState } from 'react';
import { AppShell } from '../components/AppShell';
import { PageContainer } from '../components/ui/PageContainer';
import { PageHeader } from '../components/ui/PageHeader';
import { SegmentedTabs } from '../components/ui/SegmentedTabs';
import { AboutLine } from '../components/settings/AboutLine';
import { AccessCard } from '../components/settings/AccessCard';
import { ActivityPanel } from '../components/settings/ActivityPanel';
import { MembersPanel } from '../components/settings/MembersPanel';
import { PrivacyCard } from '../components/settings/PrivacyCard';
import { ProfileCard } from '../components/settings/ProfileCard';
import { SecurityCard } from '../components/settings/SecurityCard';
import { useAuth } from '../hooks/useAuth';
import { useTenant } from '../hooks/useTenant';

/**
 * Who may manage the active organisation's members. Mirrors the API guard on
 * /tenants/:id/members: that organisation's admins, and platform admins.
 */
export const canManageMembers = (roles = [], activeTenant = null) =>
  Boolean(activeTenant?.id)
  && (activeTenant.role === 'tenant_admin' || roles.includes('system_admin'));

const TABS = [
  { id: 'account', label: 'Account' },
  { id: 'members', label: 'Members' },
  { id: 'activity', label: 'Activity' },
];

/**
 * Settings: the signed-in person's account for everyone, plus the members and
 * the activity of the active organisation for its admins.
 */
export const Settings = () => {
  const { user, roles } = useAuth();
  const { activeTenant } = useTenant();
  const [tab, setTab] = useState('account');

  const showMembers = canManageMembers(roles, activeTenant);
  const activeTab = showMembers ? tab : 'account';
  const organisation = activeTenant?.name || activeTenant?.slug;

  return (
    <AppShell title="Settings">
      <PageContainer>
        <PageHeader
          title="Settings"
          subtitle={showMembers
            ? `Your account, and the members and activity of ${organisation}.`
            : 'Your profile, sign-in and access.'}
        />

        {showMembers && (
          <div className="mb-6">
            <SegmentedTabs ariaLabel="Settings sections" tabs={TABS} active={activeTab} onChange={setTab} />
          </div>
        )}

        {activeTab === 'members' && (
          <MembersPanel key={activeTenant.id} tenant={activeTenant} currentUserId={user?.id} />
        )}
        {activeTab === 'activity' && <ActivityPanel key={activeTenant.id} tenant={activeTenant} />}
        {activeTab === 'account' && (
          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] gap-6 items-start">
              <div className="flex flex-col gap-6 min-w-0">
                <ProfileCard />
                <PrivacyCard />
              </div>
              <div className="flex flex-col gap-6 min-w-0">
                <SecurityCard />
                <AccessCard />
              </div>
            </div>
            <AboutLine />
          </div>
        )}
      </PageContainer>
    </AppShell>
  );
};
